import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/FirebaseAuthContext';
import { useGameSession } from '../context/GameSessionContext';
import AuthForm from './AuthForm';
import TutorialVideo from './TutorialVideo';
import { GAME_TYPES, GAME_TYPE_DISPLAY_NAMES } from '../config/constants';
import './UserDashboard.css';

const UserDashboard: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { currentSession, isSessionActive, startSession, endSession, getUserSessionStats } = useGameSession();
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [selectedGameType, setSelectedGameType] = useState<string>(GAME_TYPES.NUMBER_SEQUENCE);
  const [sessionStats, setSessionStats] = useState<any>(null);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const loadSessionStats = async () => {
      if (isAuthenticated) {
        try {
          const stats = await getUserSessionStats();
          setSessionStats(stats);
        } catch (error) {
          console.error('Failed to load session stats:', error);
        }
      }
    };

    loadSessionStats();
  }, [isAuthenticated, getUserSessionStats, user]);

  const handleStartSession = async () => {
    try {
      await startSession(selectedGameType, sessionNotes || undefined);
      setSessionNotes('');
      setShowSessionForm(false);
      // Refresh stats
      const stats = await getUserSessionStats();
      setSessionStats(stats);
    } catch (error) {
      console.error('Failed to start session:', error);
      alert('Failed to start session. Please try again.');
    }
  };

  const handleEndSession = async () => {
    try {
      await endSession();
    } catch (error) {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="user-dashboard">
        <div className="auth-prompt">
          <h3>Welcome to Cognitive Games</h3>
          <p>Sign in to save your progress and track your performance over time.</p>
          <button 
            className="auth-btn login-btn" 
            onClick={() => setShowAuthForm(true)}
          >
            Sign In / Register
          </button>
          <button 
            className="auth-btn" 
            onClick={() => setShowTutorial(true)}
            style={{
              marginTop: '15px',
              backgroundColor: '#4F46E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            🎥 Watch Tutorial
          </button>
        </div>
        {showAuthForm && (
          <AuthForm onClose={() => setShowAuthForm(false)} />
        )}
        {showTutorial && (
          <TutorialVideo onClose={() => setShowTutorial(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      <div className="user-info">
        <div className="user-details">
          <h3>Welcome back, {user?.displayName}!</h3>
          <p className="user-email">Email: {user?.email}</p>
          {user?.role && (
            <p className="user-institution">Role: {user.role}</p>
          )}
          <div className="user-badges">
            <span className="session-badge">User since: {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}</span>
          </div>
        </div>
        <button className="logout-btn" onClick={logout}>
          Sign Out
        </button>
      </div>

      <div className="session-stats">
        <h4>🎮 Game Session Progress</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Number Sequencing</span>
            <span className="stat-value">{sessionStats?.numberSequenceSessionCount || 0} sessions</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Memory Palace</span>
            <span className="stat-value">{sessionStats?.memoryPalaceSessionCount || 0} sessions</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tone Memory</span>
            <span className="stat-value">{sessionStats?.toneMemorySessionCount || 0} sessions</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Image Matching</span>
            <span className="stat-value">{sessionStats?.imageMatchingSessionCount || 0} sessions</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ticker Tape Rush</span>
            <span className="stat-value">{sessionStats?.tickerTapeSessionCount || 0} sessions</span>
          </div>
        </div>
      </div>

      <div className="session-controls">
        {!isSessionActive ? (
          <div className="start-session">
            <h4>Start New Game Session</h4>
            <p>Choose a game and start tracking your session</p>
            {showSessionForm ? (
              <div className="session-form">
                <div className="form-group">
                  <label htmlFor="gameType">Select Game:</label>
                  <select
                    id="gameType"
                    value={selectedGameType}
                    onChange={(e) => setSelectedGameType(e.target.value)}
                  >
                    {Object.entries(GAME_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>
                        {GAME_TYPE_DISPLAY_NAMES[value as keyof typeof GAME_TYPE_DISPLAY_NAMES]}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  placeholder="Optional notes for this session..."
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  rows={3}
                />
                <div className="session-form-buttons">
                  <button className="btn-primary" onClick={handleStartSession}>
                    Start Session
                  </button>
                  <button 
                    className="btn-secondary" 
                    onClick={() => {
                      setShowSessionForm(false);
                      setSessionNotes('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button 
                className="btn-primary" 
                onClick={() => setShowSessionForm(true)}
              >
                Start Session
              </button>
            )}
          </div>
        ) : (
          <div className="active-session">
            <h4>🎮 Active Game Session</h4>
            <div className="session-info">
              <p><strong>Game:</strong> {GAME_TYPE_DISPLAY_NAMES[currentSession?.gameType as keyof typeof GAME_TYPE_DISPLAY_NAMES] || currentSession?.gameType}</p>
              <p><strong>Session #:</strong> {currentSession?.sessionId.substring(0, 8)}</p>
              <p><strong>Session ID:</strong> {currentSession?.sessionId}</p>
              <p><strong>Started:</strong> {new Date(currentSession?.startTime || '').toLocaleString()}</p>
              {currentSession?.notes && (
                <p><strong>Notes:</strong> {currentSession.notes}</p>
              )}
            </div>
            <button className="btn-danger" onClick={handleEndSession}>
              End Session
            </button>
          </div>
        )}
      </div>

      <div className="dashboard-stats">
        <h4>Your Progress</h4>
        <p className="coming-soon">📊 Detailed statistics coming soon...</p>
      </div>
    </div>
  );
};

export default UserDashboard;